import React from "react";
import { useTranslation } from "react-i18next";
import {
  getOrderStatusByKey,
  getOrderStatusBySlug,
  getOrderStatusByValue,
} from "../../../../constants/orderStatus";
import {
  getStatusGroup,
  getStatusBadgeColor,
  STATUS_LABELS,
} from "@finitless/shared/orderConstants";

/**
 * StatusBadge component for displaying order status with appropriate colors
 * @param {Object} props - Component props
 * @param {string|number} props.status - Status value, slug, or key
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showPercentage - Whether to show percentage in the badge
 * @param {boolean} props.showIcon - Whether to show icon in the badge (default: true)
 */
const StatusBadge = ({
  status,
  className = "",
  showPercentage = false,
  showIcon = true,
}) => {
  const { t } = useTranslation("orders");

  const resolveStatusInfo = () => {
    if (typeof status === "number") {
      return getOrderStatusByKey(status);
    }

    if (typeof status === "string") {
      return (
        getOrderStatusBySlug(status) ||
        getOrderStatusBySlug(status.toUpperCase()) ||
        getOrderStatusByValue(status)
      );
    }

    return null;
  };

  // Helper function to get status color using centralized system
  const getStatusColor = (status) => {
    // First try to get color by status group
    const statusGroup = getStatusGroup(status);
    if (statusGroup !== "unknown") {
      return getStatusBadgeColor(status);
    }

    // Fallback to percentage-based logic for unknown statuses
    const statusInfo =
      getOrderStatusByKey(status) ||
      getOrderStatusBySlug(status) ||
      getOrderStatusByValue(status);
    const percentage = statusInfo?.percentage ?? 0;

    if (percentage === 100) {
      return "bg-green-500/20 text-green-400 border-green-500/30";
    } else if (percentage === 0) {
      return "bg-red-500/20 text-red-400 border-red-500/30";
    } else if (percentage >= 80) {
      return "bg-blue-500/20 text-blue-400 border-blue-500/30";
    } else if (percentage >= 50) {
      return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    } else if (percentage >= 20) {
      return "bg-orange-500/20 text-orange-400 border-orange-500/30";
    } else {
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  // Helper function to get status icon based on percentage
  const getStatusIcon = (percentage) => {
    if (percentage === 100) {
      return "✓";
    } else if (percentage === 0) {
      return "✗";
    } else if (percentage >= 80) {
      return "→";
    } else if (percentage >= 50) {
      return "⏳";
    } else {
      return "○";
    }
  };

  // Try to find the status by different methods
  let statusInfo = resolveStatusInfo();

  if (!statusInfo) {
    statusInfo = {
      value: t("status.unknown"),
      percentage: 0,
    };
  }

  const statusKeyForTranslation =
    statusInfo?.key ?? (typeof status === "number" ? status : null);
  const fallbackStatusLabel =
    STATUS_LABELS[statusKeyForTranslation] ||
    STATUS_LABELS[status] ||
    statusInfo.value;

  // Use centralized status labels for consistency with a safe fallback if key is missing.
  const statusLabel = (() => {
    if (
      statusKeyForTranslation === null ||
      statusKeyForTranslation === undefined
    ) {
      return fallbackStatusLabel;
    }

    const translationKey = `statusLabels.${statusKeyForTranslation}`;
    const translatedLabel = t(translationKey, {
      defaultValue: fallbackStatusLabel,
    });

    return translatedLabel === translationKey
      ? fallbackStatusLabel
      : translatedLabel;
  })();

  const colorClass = getStatusColor(status);
  const icon = getStatusIcon(statusInfo.percentage);
  const displayText = showPercentage
    ? `${statusLabel} (${statusInfo.percentage}%)`
    : statusLabel;

  return (
    <span
      className={`rounded-full border px-2 py-1 text-xs font-medium ${colorClass} ${className}`}
    >
      {showIcon && <span className="mr-1">{icon}</span>}
      {displayText}
    </span>
  );
};

export default StatusBadge;
